import { Component } from '@angular/core';
import Highcharts from 'highcharts';
import { ChartPoint, ChartSeries, NOT_ASKED_LABEL, chartColors } from '../report-aggregation';
import { CHART_SPACING } from './chart-layout';
import { ChartBlockBase } from './chart-block-base';

/** The plot's own box. Square-ish, because a positioning map is read as a space rather than a list. */
const MIN_HEIGHT = 360;
const MAX_HEIGHT = 560;

/** Room past the furthest dot, so a point never sits on the frame with its label outside it. */
const AXIS_HEADROOM = 1.08;

/** The axes in the deck's text colours: Highcharts writes its own greys onto them otherwise. */
const AXIS_LABEL_STYLE: Highcharts.CSSObject = { color: 'var(--ecr-ink, #1b2b33)' };
const AXIS_TITLE_STYLE: Highcharts.CSSObject = { color: 'var(--ecr-mut, #5a6b73)' };

/** One item, placed. */
interface Placed {
  name: string;
  x: number;
  y: number;
}

/**
 * Positioning scatter: two measures of the same items, plotted against each other.
 *
 * Every other module here reduces a question to a picture of itself. This one is the only place two
 * questions meet, and the whole design follows from that: the items are matched **by label**, since
 * that is the only thing two separately-asked questions share, and an item that appears in one and
 * not the other cannot be placed at all.
 *
 * Nothing about it is specific to reach or time. Each axis is whatever its own question means -- a
 * share for a multi-punch or single-choice question, a mean for a numeric battery -- and both the
 * axis titles and the quadrant caption are the study's own words.
 */
@Component({
  selector: 'app-positioning-scatter-block',
  standalone: false,
  template: `
    @if (chartOptions) {
      <highcharts-chart [Highcharts]="highcharts" [oneToOne]="true" [options]="chartOptions"
                        style="width: 100%; display: block;"></highcharts-chart>
    }

    <!-- Positions are the finding, and a position cannot be read aloud. -->
    <p class="sr-only">{{ summary }}</p>

    @for (note of notes; track note) {
      <p class="grey4 nav-font12 mb-0 mt-1">{{ note }}</p>
    }
  `,
  // Inline by default, which would make the width measured in ChartBlockBase 0.
  styles: [`
    :host { display: block; }
    .sr-only {
      position: absolute;
      width: 1px;
      height: 1px;
      padding: 0;
      margin: -1px;
      overflow: hidden;
      clip: rect(0, 0, 0, 0);
      white-space: nowrap;
      border: 0;
    }
  `],
})
export class PositioningScatterBlockComponent extends ChartBlockBase {
  readonly highcharts: typeof Highcharts = Highcharts;
  chartOptions: Highcharts.Options | null = null;
  summary = '';
  /** Why a dot is missing, or why nothing is drawn at all. Never silent. */
  notes: string[] = [];

  protected build() {
    this.chartOptions = null;
    this.summary = '';
    this.notes = [];
    if (this.block.reportType !== 'scatter-plot') return;

    const extra = this.block.reportExtraData;
    const [across, up] = this.series ?? [];

    if (!across || !up) {
      this.notes = ['This module needs two questions: one for each axis.'];
      return;
    }
    for (const axis of [across, up]) {
      if (!axis.known) this.notes.push(`No data for ${axis.name}.`);
    }
    if (this.notes.length) return;

    const placed = this.placeItems(across, up);
    if (!placed.length) {
      // Replaces the per-item note rather than adding to it: when *nothing* matched, listing every
      // item as unplaced is the same fact said twice, and the useful half is why.
      this.notes = ['No item was asked in both questions, so nothing can be placed. '
        + 'A scatter plot needs two questions asked about the same list of items.'];
      return;
    }

    const roles = chartColors(this.palette, Math.max(1, placed.length));
    const showQuadrants = extra.showQuadrants !== false;
    const meanX = mean(placed.map(item => item.x));
    const meanY = mean(placed.map(item => item.y));

    this.summary = `${placed.length} items, ${this.axisTitle(extra.xLabel, across)} against `
      + `${this.axisTitle(extra.yLabel, up)}: `
      + placed.map(item => `${item.name} ${format(item.x, across)}, ${format(item.y, up)}`).join('; ');

    this.chartOptions = this.deckMotion({
      chart: {
        type: 'scatter',
        height: Math.max(MIN_HEIGHT, Math.min(MAX_HEIGHT, Math.round(this.hostWidth() * 0.62))),
        spacing: CHART_SPACING,
        style: { fontFamily: 'inherit' },
      },
      title: { text: undefined },
      credits: { enabled: false },
      legend: { enabled: false },
      xAxis: {
        title: { text: this.axisTitle(extra.xLabel, across), style: AXIS_TITLE_STYLE },
        min: 0,
        max: Math.max(...placed.map(item => item.x)) * AXIS_HEADROOM,
        gridLineWidth: 0,
        labels: { format: across.numeric ? '{value}' : '{value}%', style: AXIS_LABEL_STYLE },
        // At the average rather than the middle: the question a positioning map answers is "which
        // of these is above average on both", and the middle of an axis answers nothing.
        plotLines: showQuadrants ? [averageLine(meanX)] : [],
      },
      yAxis: {
        title: { text: this.axisTitle(extra.yLabel, up), style: AXIS_TITLE_STYLE },
        min: 0,
        max: Math.max(...placed.map(item => item.y)) * AXIS_HEADROOM,
        gridLineWidth: 0,
        labels: { format: up.numeric ? '{value}' : '{value}%', style: AXIS_LABEL_STYLE },
        plotLines: showQuadrants ? [averageLine(meanY)] : [],
      },
      tooltip: {
        pointFormat: `${this.axisTitle(extra.xLabel, across)}: <b>{point.x:.1f}</b><br>`
          + `${this.axisTitle(extra.yLabel, up)}: <b>{point.y:.1f}</b>`,
        headerFormat: '<b>{point.key}</b><br>',
      },
      plotOptions: {
        scatter: {
          marker: { radius: this.compact ? 4 : 6, symbol: 'circle' },
          dataLabels: {
            enabled: true,
            format: '{point.name}',
            // A label that would sit on another is dropped rather than drawn over it: two names
            // overlapping is worse than one name missing, and the dot is still there to hover.
            allowOverlap: false,
            style: {
              fontSize: `${this.labelSize() - 1}px`,
              fontWeight: '400',
              textOutline: 'none',
              color: 'var(--ecr-ink, #1b2b33)',
            },
          },
        },
      },
      series: [{
        type: 'scatter',
        name: 'Items',
        data: placed.map((item, index) => ({
          name: item.name,
          x: item.x,
          y: item.y,
          colorIndex: index % roles.length,
          color: roles[index % roles.length],
        })),
      }],
      // The caption for the corner a reader looks at first. Drawn as an annotation-free axis label
      // so it needs no extra Highcharts module.
      subtitle: showQuadrants && extra.quadrantLabel?.trim()
        ? {
          text: extra.quadrantLabel.trim(),
          align: 'right',
          verticalAlign: 'top',
          style: { color: 'var(--ecr-mut, #5a6b73)', fontSize: '13px', fontWeight: '600' },
        }
        : { text: undefined },
    });
  }

  /**
   * The items with a number on both axes, matched by label.
   *
   * By label because that is all two separately-asked questions share -- there is no identifier
   * running through a multi-punch question and a numeric battery. An item in one and not the other
   * is named rather than dropped quietly: a dot missing from a positioning map is the kind of thing
   * noticed in the meeting rather than in the deck.
   */
  private placeItems(across: ChartSeries, up: ChartSeries): Placed[] {
    const byLabel = new Map(real(up).map(point => [point.label, point.percent]));
    const placed: Placed[] = [];
    const missing: string[] = [];

    for (const point of real(across)) {
      const y = byLabel.get(point.label);
      if (y === undefined) missing.push(point.label);
      else placed.push({ name: point.label, x: point.percent, y });
    }

    const drawn = new Set(placed.map(item => item.name));
    for (const point of real(up)) {
      if (!drawn.has(point.label)) missing.push(point.label);
    }

    if (missing.length) {
      this.notes.push(`Not placed, because ${missing.length === 1 ? 'it was' : 'they were'} `
        + `only asked in one of the two questions: ${[...new Set(missing)].join(', ')}.`);
    }
    return placed;
  }

  /** The axis's own words where the block gives none. */
  private axisTitle(label: string | undefined, series: ChartSeries): string {
    return label?.trim() || series.name;
  }
}

/** Real answers only: "Not asked" is a gap in a compare column, not an item to place. */
function real(series: ChartSeries): ChartPoint[] {
  return series.points.filter(point => point.optionIndex >= 0 && point.label !== NOT_ASKED_LABEL);
}

function mean(values: number[]): number {
  return values.length ? values.reduce((sum, value) => sum + value, 0) / values.length : 0;
}

function format(value: number, series: ChartSeries): string {
  return series.numeric ? value.toFixed(1) : `${Math.round(value)}%`;
}

function averageLine(at: number): Highcharts.AxisPlotLinesOptions {
  return {
    value: at,
    width: 1,
    color: 'var(--nav-grey2, #ddd)',
    dashStyle: 'Dash',
    zIndex: 1,
  };
}
